/**
 * Scope graph queries — pathname → scope, scope id → chain, etc.
 *
 * The scope tree is a static DAG built from ROUTES + each feature's
 * declared `scopes`. Dynamic leaves (a specific note, a Space instance)
 * live OUTSIDE this graph; they're surfaced by `resolveChildren` and
 * carry their own `onSelect` so navigation never depends on registry
 * traversal for things that aren't registered.
 */
import { ROUTES } from "@/shared/lib/routes";
import type { ScopeNode } from "../types";
import { FEATURE_PALETTES } from "./features";

export const ALL_SCOPES: ScopeNode[] = [
  ...ROUTES,
  ...FEATURE_PALETTES.flatMap((f) => f.scopes ?? []),
];

const scopeIndex = new Map(ALL_SCOPES.map((s) => [s.id, s]));

export function getScope(id: string): ScopeNode | undefined {
  return scopeIndex.get(id);
}

export function getChildren(parentId: string | null): ScopeNode[] {
  return ALL_SCOPES.filter((s) => s.parentId === parentId);
}

export function getScopeChain(id: string): ScopeNode[] {
  const chain: ScopeNode[] = [];
  let cur = scopeIndex.get(id);
  while (cur) {
    chain.unshift(cur);
    cur = cur.parentId ? scopeIndex.get(cur.parentId) : undefined;
  }
  return chain;
}

/** Longest-prefix match — for the "current scope" boost in the ranker. */
export function scopeFromPathname(pathname: string): ScopeNode | undefined {
  let best: ScopeNode | undefined;
  for (const s of ALL_SCOPES) {
    if (!s.href) continue;
    const exact = pathname === s.href;
    const childOf = pathname.startsWith(s.href + "/");
    const root = s.href === "/" && pathname === "/";
    if (exact || childOf || root) {
      if (!best?.href || s.href.length > best.href.length) best = s;
    }
  }
  return best;
}
